import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Stack, Box, Button,
  MenuItem, Select, FormControl, InputLabel,
  List, ListItem, ListItemText, IconButton, TextField, Alert
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../context/StudyPlansContext';
import { httpPut, httpGet } from '../lib/http';

const DIFFICULTY_HOURS = {
  easy: 5,
  medium: 8,
  hard: 12
};

export default function StudyPlanEdit() {
  const { pathId } = useParams();
  const [plan, setPlan] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [planTitle, setPlanTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { getTrack, upsertTrack, loadTracks } = useStudyPlans();

  useEffect(() => {
    async function loadPlan() {
      setLoading(true);
      try {
        // Try to get from context first
        const cached = getTrack(pathId);
        if (cached) {
          setPlan(cached);
          setPlanTitle(cached.title || '');
          const subs = (cached.subjects || cached.orderedSubjects || []).map((s, i) => ({
            title: s.title,
            difficulty: s.difficulty || 'medium',
            estimatedHours: s.estimatedHours || DIFFICULTY_HOURS.medium,
            order: s.order !== undefined ? s.order : i,
            chapters: s.chapters || null
          }));
          setSubjects(subs);
        } else {
          // Fetch from API
          const userId = user?.id || user?.userId || user?._id;
          const res = await httpGet(`/api/users/${encodeURIComponent(userId)}/study-tracks/${encodeURIComponent(pathId)}`);
          if (res && res.ok) {
            const data = await res.json();
            const trackData = data?.track || data?.data || data;
            setPlan(trackData);
            setPlanTitle(trackData.title || '');
            const subs = (trackData.subjects || trackData.orderedSubjects || []).map((s, i) => ({
              title: s.title,
              difficulty: s.difficulty || 'medium',
              estimatedHours: s.estimatedHours || DIFFICULTY_HOURS.medium,
              order: s.order !== undefined ? s.order : i,
              chapters: s.chapters || null
            }));
            setSubjects(subs);
          } else {
            setError('Failed to load study plan');
          }
        }
      } catch (err) {
        setError('Error loading study plan: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    }

    if (isAuthenticated && user) {
      loadPlan();
    }
  }, [pathId, isAuthenticated, user, getTrack]);

  const moveSubject = (index, dir) => {
    const copy = [...subjects];
    const target = index + dir;
    if (target < 0 || target >= copy.length) return;

    [copy[index], copy[target]] = [copy[target], copy[index]];
    setSubjects(copy.map((s, i) => ({ ...s, order: i })));
  };

  const updateDifficulty = (index, difficulty) => {
    const copy = [...subjects];
    copy[index] = {
      ...copy[index],
      difficulty,
      estimatedHours: DIFFICULTY_HOURS[difficulty]
    };
    setSubjects(copy);
  };

  const removeSubject = (index) => {
    const copy = subjects.filter((_, i) => i !== index);
    setSubjects(copy.map((s, i) => ({ ...s, order: i })));
  };

  const handleSave = async () => {
    if (!isAuthenticated || !user) return;
    if (saving) return;

    setSaving(true);
    setError(null);

    const userId = user?.id || user?.userId || user?._id;

    // Build updated payload
    const payload = {
      ...plan,
      title: planTitle,
      subjects: subjects,
      totalEstimatedHours: subjects.reduce((a, b) => a + b.estimatedHours, 0),
      updatedAt: new Date().toISOString()
    };

    try {
      const res = await httpPut(
        `/api/users/${encodeURIComponent(userId)}/study-tracks/${encodeURIComponent(pathId)}`,
        payload
      );
      
      if (res && res.ok) {
        const data = await res.json();
        const track = data?.track || data?.data || data;
        if (track && upsertTrack) {
          upsertTrack(track);
        }
        // Reload tracks to get fresh data
        await loadTracks(userId);
        navigate(`/study-paths/${pathId}`);
      } else {
        setError('Failed to save changes');
      }
    } catch (err) {
      setError('Error saving changes: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography>Loading study plan...</Typography>
      </Container>
    );
  }

  if (error && !plan) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/study-paths/mine')}>
          Back to My Plans
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Edit Study Plan
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={3}>
        {/* Plan Title */}
        <TextField
          fullWidth
          label="Plan Title"
          value={planTitle}
          onChange={(e) => setPlanTitle(e.target.value)}
          helperText="Give your study plan a meaningful name"
        />

        {/* Subjects */}
        {subjects.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Subjects</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reorder subjects, adjust difficulty levels, or remove subjects you don't need.
            </Typography>

            <List>
              {subjects.map((s, i) => (
                <ListItem
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    p: 2
                  }}
                >
                  <ListItemText
                    primary={<Typography fontWeight={700}>{s.title}</Typography>}
                    secondary={`Estimated: ${s.estimatedHours} hours`}
                  />
                  <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Difficulty</InputLabel>
                      <Select
                        size="small"
                        label="Difficulty"
                        value={s.difficulty}
                        onChange={(e) => updateDifficulty(i, e.target.value)}
                      >
                        <MenuItem value="easy">Easy (5 hrs)</MenuItem>
                        <MenuItem value="medium">Medium (8 hrs)</MenuItem>
                        <MenuItem value="hard">Hard (12 hrs)</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => moveSubject(i, -1)}
                      disabled={i === 0}
                      aria-label="move up"
                    >
                      <ArrowUpward />
                    </IconButton>
                    <IconButton
                      onClick={() => moveSubject(i, 1)}
                      disabled={i === subjects.length - 1}
                      aria-label="move down"
                    >
                      <ArrowDownward />
                    </IconButton>
                    <IconButton
                      onClick={() => removeSubject(i)}
                      aria-label="remove"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Total Estimated Hours: <strong>{subjects.reduce((a, b) => a + b.estimatedHours, 0)}</strong>
              </Typography>
            </Box>
          </Box>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => navigate(`/study-paths/${pathId}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !planTitle.trim() || subjects.length === 0}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
